import React, { useState } from 'react';

import { createTheme, ThemeProvider, Tooltip as MaterialTooltip } from '@mui/material';
import { TooltipProps } from '@mui/material/Tooltip/Tooltip';

import useMobileCheck from 'hooks/useMobileCheck';

interface TooltipWrapperProps extends TooltipProps {
  title: NonNullable<React.ReactNode>;
  children: JSX.Element;
  isOpen?: boolean;
  tooltipRef?: React.Ref<HTMLInputElement>;
  isImage?: boolean;
}

const getTheme = (isMobile: boolean, isImage = false) => {
  return createTheme({
    components: {
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            fontFamily: 'Roboto',
            fontStyle: 'normal',
            fontWeight: '400',
            fontSize: '14px',
            lineHeight: '18px',
            padding: '10px',
            color: '#070a0d',
            backgroundColor: '#ffffff',
            whiteSpace: 'pre-wrap',
            border: '1px solid #c3c8cd',
            borderRadius: '5px',
            ...(!isImage
              ? {
                  maxWidth: isMobile ? 'calc(100% - 50px)' : '300px',
                  maxHeight: isMobile ? '250px' : '500px',
                  margin: '5px !important',
                  overflow: 'auto',
                }
              : {
                  maxWidth: 'none',
                  maxHeight: 'none',
                  padding: 0,
                }),
          },
          arrow: {
            '&::before': {
              border: '1px solid #c3c8cd',
              backgroundColor: '#fff',
              boxSizing: 'border-box',
            },
          },
        },
      },
    },
  });
};

const DELAY_BEFORE_CLOSE = 30000;

const Tooltip = ({
  title,
  children,
  isOpen = false,
  tooltipRef,
  isImage = false,
  ...restProps
}: TooltipWrapperProps) => {
  const [open, setOpen] = useState(isOpen);

  const handleTooltipClose = () => {
    setOpen(false);
  };

  const handleTooltipOpen = () => {
    if (title) {
      setOpen(true);
    }
  };

  const isMobile = useMobileCheck();

  return (
    <ThemeProvider theme={getTheme(isMobile, isImage)}>
      <MaterialTooltip
        onMouseOver={handleTooltipOpen}
        onTouchStart={handleTooltipOpen}
        onClose={handleTooltipClose}
        onClick={handleTooltipOpen}
        open={open}
        placement={!isMobile ? 'right' : undefined}
        arrow
        title={title}
        leaveTouchDelay={DELAY_BEFORE_CLOSE}
        ref={tooltipRef}
        {...restProps}
      >
        {children}
      </MaterialTooltip>
    </ThemeProvider>
  );
};

export default Tooltip;
